import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, ChevronLeft, ChevronRight, RefreshCw, Search, X } from 'lucide-react';
import { SkeletonTable } from '../components/Skeleton';
import { FilterSearchInput } from '../components/FormComponents';
import SortableHeader from '../components/SortableHeader';
import { fetchCounterparties, counterpartyPath } from '../features/counterparties/counterpartyApi';
import { CounterpartyRow, EmptyBlock } from '../features/counterparties/CounterpartySections';

const KIND_OPTIONS = [
  { value: '', label: 'Groups and counterparties' },
  { value: 'group', label: 'Groups only' },
  { value: 'counterparty', label: 'Counterparties only' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Counterparty index (doc-174). One row per relationship — the entity is the
// counterparty_group where one exists, otherwise the counterparty, with every
// spelling folded in by the backend.
export default function CounterpartiesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryToken, setRetryToken] = useState(0);

  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [kind, setKind] = useState(searchParams.get('kind') || '');
  const [sortField, setSortField] = useState(searchParams.get('sort') || 'contracts');
  const [page, setPage] = useState(parseInt(searchParams.get('page'), 10) || 1);
  const [pageSize, setPageSize] = useState(() => {
    const stored = localStorage.getItem('counterpartiesPageSize');
    return stored ? parseInt(stored, 10) : 25;
  });

  // Filters live in the URL so a filtered view is shareable and the back button
  // works, matching Contracts/TPRM/Reviews.
  useEffect(() => {
    const next = new URLSearchParams();
    if (search.trim()) next.set('q', search.trim());
    if (kind) next.set('kind', kind);
    if (sortField && sortField !== 'contracts') next.set('sort', sortField);
    if (page > 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [search, kind, sortField, page, setSearchParams]);

  const filtersTouchedRef = useRef(false);
  useEffect(() => {
    if (!filtersTouchedRef.current) {
      filtersTouchedRef.current = true;
      return;
    }
    setPage(1);
  }, [search, kind, sortField]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Debounced (search fires per keystroke) + stale-response guarded.
    const timer = setTimeout(() => {
      fetchCounterparties({ q: search, kind, sort: sortField, page, pageSize })
        .then((data) => {
          if (cancelled) return;
          setRows(data.counterparties || []);
          setPagination(data.pagination || { total: 0, totalPages: 1 });
          // The backend clamps an out-of-range page to one it can actually
          // serve; adopt what it served so a deep-linked ?page=9 over 1 page of
          // results shows page 1 with a working pager instead of a rowless dead
          // end. Adopting also self-heals the querystring through the sync
          // effect above. (doc-188 defect 4)
          const servedPage = data.pagination && data.pagination.page;
          if (servedPage && servedPage !== page) setPage(servedPage);
          setError('');
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err.message);
          setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, kind, sortField, page, pageSize, retryToken]);

  const hasFilters = Boolean(search.trim() || kind);
  const clearAllFilters = () => { setSearch(''); setKind(''); };
  const activeFilters = [
    search.trim() && { key: 'q', label: `Search: ${search.trim()}`, onRemove: () => setSearch('') },
    kind && {
      key: 'kind',
      label: KIND_OPTIONS.find((o) => o.value === kind)?.label,
      onRemove: () => setKind(''),
    },
  ].filter(Boolean);

  // One direction per column: the backend sorts name ascending and the count /
  // recency columns descending, which is the only useful direction for each.
  const toggleSort = (field) => setSortField(field);

  const handlePageSizeChange = (n) => {
    setPageSize(n);
    localStorage.setItem('counterpartiesPageSize', String(n));
    setPage(1);
  };

  // doc-230: the skeleton is FIRST-LOAD only. Rendering it on every `loading`
  // unmounted the table — sortable headers included — on each refetch, so the
  // sort button a keyboard user had just activated was destroyed and focus fell
  // to <body>; a second Space/Enter then did nothing. A refetch over rows we
  // already have keeps the table mounted and marks it aria-busy instead.
  const firstLoad = loading && rows.length === 0;
  const refreshing = loading && rows.length > 0 && !error;

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Workspace</div>
          <h1 className="page-title">Counterparties</h1>
          <div className="page-subtitle">
            One page per relationship — contracts, TPRM requests, upcoming dates and activity.
            Spellings and group members are folded together.
          </div>
        </div>
        <button className="btn outline sm" onClick={() => setRetryToken((t) => t + 1)} disabled={loading}>
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FilterSearchInput
            className="filter-search-wide"
            icon={Search}
            placeholder="Search counterparties and groups…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-search-input
          />
        </div>
      </div>

      <div className="filter-row natural" style={{ marginBottom: activeFilters.length ? 10 : 16 }}>
        {/* Option keys are prefix + value + index (doc-339): the list leads with
            an empty-valued "All …" placeholder, so keying on the value alone
            means a second empty-valued entry collides with it and React
            silently drops one. */}
        <select className="filter-select" value={kind} onChange={(e) => setKind(e.target.value)}>
          {KIND_OPTIONS.map((o, i) => (
            <option key={`kind-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {activeFilters.length > 0 && (
        <div className="active-filters" style={{ marginBottom: 16 }}>
          {activeFilters.map((f) => (
            <span key={f.key} className="active-filter-chip">
              {f.label}
              <button type="button" onClick={f.onRemove} aria-label={`Remove ${f.label}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button className="btn ghost xs" onClick={clearAllFilters}>
            <X className="w-3 h-3" />
            Clear all
          </button>
        </div>
      )}

      {firstLoad ? (
        <SkeletonTable rows={6} />
      ) : error ? (
        <div className="callout red">
          <div className="callout-body">
            <strong>Error loading counterparties.</strong> {error}
            <div style={{ marginTop: 10 }}>
              <button className="btn outline sm" onClick={() => setRetryToken((t) => t + 1)}>
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <EmptyBlock
            icon={Building2}
            title={hasFilters ? 'No matching counterparties' : 'No counterparties yet'}
            hint={hasFilters
              ? 'Try clearing the filters.'
              : 'Counterparties appear here as soon as a contract or a TPRM request names one.'}
          />
        </div>
      ) : (
        <div className="card">
          <div
            style={{ overflowX: 'auto' }}
            className={refreshing ? 'list-refreshing' : undefined}
            aria-busy={refreshing || undefined}
          >
            <table className="tbl">
              <thead>
                <tr>
                  <SortableHeader field="name" sortField={sortField} sortOrder="asc" onToggle={toggleSort}>
                    Counterparty
                  </SortableHeader>
                  <SortableHeader field="contracts" sortField={sortField} sortOrder="desc" onToggle={toggleSort} style={{ width: 150 }}>
                    Contracts
                  </SortableHeader>
                  <SortableHeader field="requests" sortField={sortField} sortOrder="desc" onToggle={toggleSort} style={{ width: 160 }}>
                    TPRM requests
                  </SortableHeader>
                  <SortableHeader field="updated" sortField={sortField} sortOrder="desc" onToggle={toggleSort} style={{ width: 130 }}>
                    Last activity
                  </SortableHeader>
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => (
                  <tr
                    key={entry.key}
                    onClick={() => navigate(counterpartyPath(entry.key))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(counterpartyPath(entry.key));
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                  >
                    <CounterpartyRow entry={entry} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div
              style={{
                padding: '10px 24px',
                borderTop: '0.5px solid var(--border-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 12,
                color: 'var(--text-tertiary)',
              }}
            >
              <div className="row" style={{ gap: 16 }}>
                <span className="tnum">
                  {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, pagination.total)} of {pagination.total}
                </span>
                <div className="row" style={{ gap: 6 }}>
                  <span>Per page</span>
                  <select
                    className="filter-select"
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                    style={{ height: 24, padding: '0 22px 0 8px', fontSize: 11 }}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row" style={{ gap: 4 }}>
                <button
                  className="icon-btn xs"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Previous page"
                  style={{ opacity: page === 1 ? 0.4 : 1 }}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="mono tnum" style={{ minWidth: 56, textAlign: 'center' }}>
                  {page} / {pagination.totalPages}
                </span>
                <button
                  className="icon-btn xs"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                  aria-label="Next page"
                  style={{ opacity: page >= pagination.totalPages ? 0.4 : 1 }}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
